import React, { useState, useEffect, useCallback } from 'react';
import {
  getShiftTemplates, createShiftTemplate, updateShiftTemplate, deleteShiftTemplate,
  assignShiftTemplate, unassignShiftTemplate, getEmployees, getTeams,
} from '../../api';

const DAYS = [{ n: 1, l: 'Mon' }, { n: 2, l: 'Tue' }, { n: 3, l: 'Wed' }, { n: 4, l: 'Thu' }, { n: 5, l: 'Fri' }, { n: 6, l: 'Sat' }, { n: 0, l: 'Sun' }];
const COLORS = ['#6d5efc', '#06b6d4', '#16a34a', '#d97706', '#dc2626', '#db2777', '#0ea5e9', '#7c3aed'];
const inp = { width: '100%', padding: '9px 11px', border: '1px solid var(--glass-border)', borderRadius: 8, fontSize: 14, background: 'var(--glass-2)', color: 'var(--text)', boxSizing: 'border-box' };
const lbl = { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text)', margin: '14px 0 6px' };
const daysLabel = (arr) => (arr && arr.length) ? DAYS.filter(d => arr.includes(d.n)).map(d => d.l).join(', ') : '—';

// Create / edit a shift template.
function TemplateModal({ initial, onClose, onSaved }) {
  const editing = !!initial;
  const [form, setForm] = useState(() => ({
    name: initial?.name || '',
    start_time: initial?.start_time || '09:00',
    end_time: initial?.end_time || '17:00',
    days: initial?.days || [1, 2, 3, 4, 5],
    color: initial?.color || COLORS[0],
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleDay = (n) => set('days', form.days.includes(n) ? form.days.filter(x => x !== n) : [...form.days, n]);

  const save = async () => {
    if (!form.name.trim()) { setErr('Name is required.'); return; }
    if (!form.days.length) { setErr('Pick at least one day.'); return; }
    setBusy(true); setErr('');
    try {
      if (editing) await updateShiftTemplate(initial.id, form);
      else await createShiftTemplate(form);
      onSaved();
    } catch (e) { setErr(e.response?.data?.error || 'Failed to save'); }
    setBusy(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div className="modal-title" style={{ display: 'flex', justifyContent: 'space-between' }}>
          {editing ? 'Edit Shift' : 'Add Shift'}
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--muted)' }}>×</button>
        </div>
        <label style={lbl}>Shift name <span style={{ color: '#dc2626' }}>*</span></label>
        <input style={inp} value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ex: Morning Shift" autoFocus />
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}><label style={lbl}>Start</label><input type="time" style={inp} value={form.start_time} onChange={e => set('start_time', e.target.value)} /></div>
          <div style={{ flex: 1 }}><label style={lbl}>End</label><input type="time" style={inp} value={form.end_time} onChange={e => set('end_time', e.target.value)} /></div>
        </div>
        <label style={lbl}>Working days</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {DAYS.map(d => (
            <button key={d.n} onClick={() => toggleDay(d.n)} type="button"
              style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--glass-border)', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                background: form.days.includes(d.n) ? form.color : 'var(--glass-2)', color: form.days.includes(d.n) ? '#fff' : 'var(--text)' }}>
              {d.l}
            </button>
          ))}
        </div>
        <label style={lbl}>Color</label>
        <div style={{ display: 'flex', gap: 8 }}>
          {COLORS.map(c => (
            <button key={c} onClick={() => set('color', c)} type="button" aria-label={c}
              style={{ width: 26, height: 26, borderRadius: '50%', background: c, border: form.color === c ? '3px solid var(--text)' : '2px solid var(--glass-border)', cursor: 'pointer' }} />
          ))}
        </div>
        {err && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 12 }}>{err}</div>}
        <button onClick={save} disabled={busy} style={{ width: '100%', marginTop: 18, background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '11px', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
          {busy ? 'Saving…' : editing ? 'Save Changes' : 'Create Shift'}
        </button>
      </div>
    </div>
  );
}

// Assign a template to all / a team / specific employees.
function AssignModal({ template, employees, teams, onClose, onSaved }) {
  const [scope, setScope] = useState('employees');
  const [teamId, setTeamId] = useState('');
  const [selected, setSelected] = useState(template.members || []);
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const save = async () => {
    setBusy(true);
    try {
      if (scope === 'all') await assignShiftTemplate(template.id, { assign_scope: 'all' });
      else if (scope === 'team') await assignShiftTemplate(template.id, { assign_scope: 'team', team_id: teamId });
      else {
        // sync exact selection: assign chosen, unassign de-selected members
        const removed = (template.members || []).filter(id => !selected.includes(id));
        if (removed.length) await unassignShiftTemplate(removed);
        if (selected.length) await assignShiftTemplate(template.id, { employee_ids: selected });
      }
      onSaved();
    } catch {}
    setBusy(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 440, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="modal-title" style={{ display: 'flex', justifyContent: 'space-between' }}>
          Assign “{template.name}”
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--muted)' }}>×</button>
        </div>
        <label style={lbl}>Assign to</label>
        <select style={inp} value={scope} onChange={e => setScope(e.target.value)}>
          <option value="employees">Specific employees</option>
          <option value="team">A whole team</option>
          <option value="all">All employees</option>
        </select>
        {scope === 'team' && (
          <>
            <label style={lbl}>Team</label>
            <select style={inp} value={teamId} onChange={e => setTeamId(e.target.value)}>
              <option value="">Select a team…</option>
              {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </>
        )}
        {scope === 'employees' && (
          <div style={{ marginTop: 12, maxHeight: 220, overflowY: 'auto', border: '1px solid var(--hairline)', borderRadius: 8, padding: 8 }}>
            {employees.map(e => (
              <label key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px', fontSize: 13.5, cursor: 'pointer' }}>
                <input type="checkbox" checked={selected.includes(e.id)} onChange={() => toggle(e.id)} />
                {e.name}{e.shift_template_id && e.shift_template_id !== template.id ? <span style={{ color: 'var(--muted)', fontSize: 11 }}> (has another shift)</span> : ''}
              </label>
            ))}
          </div>
        )}
        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>
          Assigning replaces an employee's current shift. Each employee has one active shift.
        </div>
        <button onClick={save} disabled={busy || (scope === 'team' && !teamId)} style={{ width: '100%', marginTop: 16, background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '11px', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
          {busy ? 'Assigning…' : 'Assign'}
        </button>
      </div>
    </div>
  );
}

export default function ShiftSettings() {
  const [templates, setTemplates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [teams, setTeams] = useState([]);
  const [modal, setModal] = useState(null);   // null | {} | template (edit)
  const [assign, setAssign] = useState(null);  // template to assign
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try {
      const [t, e, tm] = await Promise.all([getShiftTemplates(false), getEmployees(), getTeams().catch(() => [])]);
      setTemplates(t); setEmployees(e); setTeams(tm);
    } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);
  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 2500); };

  const remove = async (t) => { if (!window.confirm(`Delete shift "${t.name}"? Employees on it will have no assigned shift.`)) return; await deleteShiftTemplate(t.id); flash('Deleted.'); load(); };

  return (
    <div style={{ maxWidth: 820 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <h2 style={{ fontSize: '1.4rem', fontWeight: 700 }}>Shift Settings</h2>
        <button onClick={() => setModal({})} style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontWeight: 600, cursor: 'pointer' }}>+ Add Shift</button>
      </div>
      <p style={{ color: 'var(--muted)', margin: '0 0 18px', fontSize: 14 }}>
        Define reusable shifts and assign them to employees. Assigned shifts auto-fill the <strong>Shift Schedule</strong> on their working days.
      </p>
      {msg && <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#16a34a', borderRadius: 8, padding: '8px 12px', marginBottom: 16, fontSize: 13 }}>{msg}</div>}

      {templates.length === 0 ? (
        <div className="card" style={{ color: 'var(--muted)', textAlign: 'center', padding: 36 }}>No shifts yet. Click “Add Shift” to create one.</div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {templates.map(t => (
            <div key={t.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 16 }}>
              <div style={{ width: 6, alignSelf: 'stretch', borderRadius: 4, background: t.color }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '1rem' }}>{t.name}</div>
                <div style={{ color: 'var(--muted)', fontSize: 13 }}>🕐 {t.start_time}–{t.end_time} · {daysLabel(t.days)}</div>
                <div style={{ color: 'var(--text-2)', fontSize: 12, marginTop: 2 }}>{(t.members || []).length} employee{(t.members || []).length === 1 ? '' : 's'} assigned</div>
              </div>
              <button onClick={() => setAssign(t)} className="btn" style={{ padding: '7px 14px' }}>Assign</button>
              <button onClick={() => setModal(t)} className="btn" style={{ padding: '7px 12px' }}>Edit</button>
              <button onClick={() => remove(t)} className="btn" style={{ padding: '7px 12px', color: '#dc2626', borderColor: '#fecaca' }}>Delete</button>
            </div>
          ))}
        </div>
      )}

      {modal && <TemplateModal initial={modal.id ? modal : null} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); flash('Saved.'); }} />}
      {assign && <AssignModal template={assign} employees={employees} teams={teams} onClose={() => setAssign(null)} onSaved={() => { setAssign(null); load(); flash('Assigned.'); }} />}
    </div>
  );
}
